"use client";

import { createLogger } from "@/lib/logger";
import { supabase } from "@/lib/supabaseClient";
import { User } from "@supabase/supabase-js";
import { useRouter } from "next/navigation";
import { createContext, useContext, useEffect, useState } from "react";

const logger = createLogger("auth");

type AuthContextType = {
  user: User | null;
  isLoading: boolean;
  clearAuthState: () => Promise<void>; // Add method to clear auth state
  isAuthModalOpen: boolean;
  authModalMode: "signin" | "signup";
  openAuthModal: (mode?: "signin" | "signup") => void;
  closeAuthModal: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasHandledRedirect, setHasHandledRedirect] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<"signin" | "signup">("signup");

  const openAuthModal = (mode: "signin" | "signup" = "signup") => {
    setAuthModalMode(mode);
    setIsAuthModalOpen(true);
  };
  const router = useRouter();

  // Enhanced session cleanup function
  const clearAuthState = async () => {
    try {
      // Only clear local state - let Supabase handle its own session management
      setUser(null);
    } catch (error) {
      logger.error("Error clearing auth state", {
        error: error instanceof Error ? error.message : String(error),
      });
      // Force clear user state even if signOut fails
      setUser(null);
    }
  };

  // Enhanced session validation with proper error handling
  const validateSession = async () => {
    try {
      // Get the current session with proper error handling
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();

      if (error) {
        logger.error("Error getting session", { error: error.message });
        // Only clear auth state for critical auth errors that cannot be recovered
        if (
          error.message?.includes("Invalid Refresh Token") ||
          error.message?.includes("Refresh Token Not Found") ||
          error.message?.includes("session expired")
        ) {
          logger.warn("Critical auth error detected, clearing auth state");
          await clearAuthState();
        }
        return null;
      }

      // If there is a session, trust Supabase's local session user
      return session?.user ?? null;
    } catch (error) {
      logger.error("Error validating session", {
        error: error instanceof Error ? error.message : String(error),
      });
      // Do not aggressively clear auth state on unexpected errors to avoid logging users out unnecessarily
      return null;
    }
  };

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        const validatedUser = await validateSession();

        if (mounted) {
          setUser(validatedUser);
          setIsInitialized(true);
        }
      } catch (error) {
        logger.error("Error initializing auth", {
          error: error instanceof Error ? error.message : String(error),
        });
        if (mounted) {
          setUser(null);
          setIsInitialized(true);
        }
      }
    };

    initializeAuth();

    // Listen for auth changes with enhanced error handling
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      logger.debug("Auth state changed", { event, hasSession: !!session });

      if (!mounted) return;

      try {
        // Handle specific auth events
        switch (event) {
          case "SIGNED_OUT":
            setUser(null);
            break;

          case "TOKEN_REFRESHED":
            // Token was refreshed, update user from the new session directly
            setUser(session?.user || null);
            break;

          case "SIGNED_IN":
            // For sign in, update user and check for pending redirect
            setUser(session?.user || null);

            // Handle stored redirect from OAuth flow (only once)
            if (session?.user && !hasHandledRedirect) {
              const storedRedirect = localStorage.getItem("auth_redirect");
              if (storedRedirect) {
                localStorage.removeItem("auth_redirect");
                setHasHandledRedirect(true);
                // Small delay to ensure state is updated
                setTimeout(() => {
                  router.push(storedRedirect);
                }, 100);
              }
            }
            break;

          case "USER_UPDATED":
          case "INITIAL_SESSION":
          case "PASSWORD_RECOVERY":
            // For positive events, update user if session exists
            setUser(session?.user || null);
            break;

          default:
            // Never call auth APIs from this callback: it runs under the SDK lock.
            setUser(session?.user || null);
            break;
        }
      } catch (error) {
        logger.error("Error handling auth state change", {
          error: error instanceof Error ? error.message : String(error),
        });
        // Don't automatically clear auth state - let Supabase handle it
        setUser(session?.user || null);
      }
    });

    return () => {
      mounted = false;
      authListener?.subscription.unsubscribe();
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading: !isInitialized,
        clearAuthState,
        isAuthModalOpen,
        authModalMode,
        openAuthModal,
        closeAuthModal: () => setIsAuthModalOpen(false),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
